import { Box, CircularProgress, Stack } from "@mui/material";
import { useEffect, useState } from "react";
import useAxiosInstance from "../../hooks/useAxiosInstance";
import { API_RESULT, API_VERSION } from "../../enums/common.enum";
import { handleError, textCapitalize } from "../../utils/common";
import {
  Card,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  TableCell,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import MUIDataTable from "mui-datatables";
import { commonTableOptions } from "../../constants";
import { LoadingButton } from "@mui/lab";
import { useIconStyles } from "../../components/Helpers/iconStyles";
import { useDispatch, useSelector } from "react-redux";
import { setAllSupplierPurchase } from "../../store/actions/useActions";
import SupplierPaymentButton from "./paymentButton";
import { PurchaseDetails } from "./purchaseDetails";

const Suppliers = () => {
  const [searchBy, setSearchBy] = useState<string>("location_id");
  const [inputValue, setInputValue] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(false);
  const [showNoDataMessage, setShowNoDataMessage] = useState(false);
  const privateInstance = useAxiosInstance();
  const [showSinglePurchaseDetail, setShowSinglePurchaseDetail] =
    useState(false);
  const [activeInvId, setActiveInvId] = useState("");

  const iconClasses = useIconStyles();
  const dispatch = useDispatch();
  let invoiceToManage = useSelector(
    (state: any) => state.supplier.allPurchaseData
  );

  const getProviderData = async () => {
    setLoading(true);
    try {
      const result = await privateInstance.get(
        `${API_VERSION.V3}/suppliers/purchases?${searchBy}=${inputValue}`
      );
      if (result?.data?.status === API_RESULT.SUCCESS) {
        if (result.data.response.length !== 0) {
          dispatch(setAllSupplierPurchase(result.data.response));
          setShowNoDataMessage(false);
        } else {
          dispatch(setAllSupplierPurchase([]));
          setShowNoDataMessage(true);
        }
      }
    } catch (err) {
      dispatch(setAllSupplierPurchase([]));
      handleError(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // eslint-disable-next-line react-hooks/exhaustive-deps
    return () => {
      dispatch(setAllSupplierPurchase([]));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const purchaseColumns = [
    {
      name: "purchase_id",
      label: "PURCHASE ID",
      options: {
        display: false,
        filter: false,
      },
    },
    {
      name: "invoice_id",
      label: "INVOICE ID",
      options: {
        filter: false,
      },
    },
    {
      name: "invoice_date",
      label: "INVOICE DATE",
      options: {
        filter: false,
        display: false,
        searchable: false,
      },
    },
    {
      name: "supplier_name",
      label: "SUPPLIER NAME",
      options: {
        filter: false,
      },
    },
    {
      name: "beneficiary_details.payment_mode",
      label: "BENEFICIARY DETAILS",
      options: {
        filter: false,
        display: true,
        searchable: false,
      },
    },
    {
      name: "beneficiary_details.bank",
      label: "BENEFICIARY DETAILS",
      options: {
        filter: false,
        display: false,
        searchable: false,
      },
    },
    {
      name: "beneficiary_details.account_no",
      label: "BENEFICIARY DETAILS",
      options: {
        filter: false,
        display: false,
        searchable: false,
      },
    },
    {
      name: "beneficiary_details.ifsc_code",
      label: "BENEFICIARY DETAILS",
      options: {
        filter: false,
        display: false,
        searchable: false,
      },
    },
    {
      name: "beneficiary_details.account_type",
      label: "BENEFICIARY DETAILS",
      options: {
        filter: false,
        display: false,
        searchable: false,
      },
    },
    {
      name: "beneficiary_details.upi_id",
      label: "BENEFICIARY DETAILS",
      options: {
        filter: false,
        display: false,
        searchable: false,
      },
    },
    {
      name: "payable_amount",
      label: "AMOUNT",
      options: {
        filter: true,
        display: true,
        searchable: false,
      },
    },
    {
      name: "payment_credit_status",
      label: "PAYMENT CREDIT STATUS",
      options: {
        filter: false,
        display: false,
        searchable: false,
      },
    },
    {
      name: "payout_id",
      label: "PAYOUT ID",
      options: {
        filter: false,
        display: false,
        searchable: false,
      },
    },
    {
      name: "",
      label: "ACTION",
    },
  ];

  const options: any = {
    textLabels: {
      body: {
        noMatch: loading ? (
          <CircularProgress sx={{ color: "#0058ff" }} />
        ) : (
          <Typography variant="body2">No Invoice Found</Typography>
        ),
      },
    },
    ...commonTableOptions,
    customRowRender: (data: any, dataIndex: number) => {
      return (
        <CustomRowComponent key={data[0]} rowData={data} index={dataIndex} />
      );
    },
    searchPlaceholder: "Invoice ID",
    filter: false,
  };

  function CustomRowComponent({ rowData, index }: any) {
    const [
      purchase_id,
      invoice_id,
      invoice_date,
      supplier_name,
      payment_mode,
      bank,
      account_no,
      ifsc_code,
      account_type,
      upi_id,
      payable_amount,
      payment_credit_status,
      payout_id,
    ] = rowData;
    return (
      <TableRow key={purchase_id} hover>
        <TableCell sx={{ width: { sm: "auto", md: "20%" } }}>
          <Stack>
            <Typography
              variant="body2"
              sx={{ color: "#0058ff", cursor: "pointer", mr: 0.25 }}
              onClick={() => {
                setActiveInvId(purchase_id);
                setShowSinglePurchaseDetail(true);
              }}
            >
              {purchase_id}
            </Typography>
            <Typography variant="body2" sx={{ color: "#718096" }}>
              Invoice ID : {invoice_id}
            </Typography>
            <Typography variant="body2" sx={{ color: "#718096" }}>
              Invoice Date : {invoice_date}
            </Typography>
          </Stack>
        </TableCell>
        <TableCell sx={{ width: { sm: "auto", md: "26%" } }}>
          <Stack direction="column">
            <Typography variant="body2">{supplier_name}</Typography>
          </Stack>
        </TableCell>
        <TableCell sx={{ width: { sm: "auto", md: "28%" } }}>
          <Stack direction="column">
            <Typography variant="body2">
              Payment Mode : {textCapitalize(payment_mode)}
            </Typography>
            {payment_mode === "BANK" ? (
              <Stack>
                <Typography variant="body2" color="#718096">
                  Bank: {bank}
                </Typography>
                <Typography variant="body2" color="#718096">
                  Account No: {account_no}
                </Typography>
                <Typography variant="body2" color="#718096">
                  IFSC Code: {ifsc_code}
                </Typography>
                <Typography variant="body2" color="#718096">
                  Account Type: {account_type}
                </Typography>
              </Stack>
            ) : (
              <Typography variant="body2" color="#718096">
                UPI ID: {upi_id}
              </Typography>
            )}
          </Stack>
        </TableCell>
        <TableCell sx={{ width: { sm: "auto", md: "18%" } }}>
          <Stack direction="column">
            <Typography variant="body2">₹{payable_amount}/-</Typography>
          </Stack>
        </TableCell>
        <TableCell align="center">
          <SupplierPaymentButton
            activePurchase={purchase_id}
            activePayoutId={payout_id}
            index={index}
          />
        </TableCell>
      </TableRow>
    );
  }

  return (
    <div>
      <Card
        sx={{
          p: 3,
          mb: 3,
          boxShadow: "0px 4px 16px rgba(0, 0, 0, 0.15)",
        }}
      >
        <Stack mt={2} direction="row">
          <FormControl size="small" fullWidth sx={{ mt: 1.5 }}>
            <InputLabel id="search-by-label">Search By</InputLabel>
            <Select
              labelId="search-by-label"
              name="search_by"
              id="search_by"
              value={searchBy}
              label="Search By"
              onChange={(e: SelectChangeEvent) => {
                setSearchBy(e.target.value);
              }}
            >
              <MenuItem value="location_id">Location ID</MenuItem>
              {/* <MenuItem value="supplier_id">Supplier ID</MenuItem>
              <MenuItem value="supplier_gst">Supplier GST</MenuItem> */}
            </Select>
          </FormControl>
          <TextField
            fullWidth
            id="value"
            label="Value"
            size="small"
            value={inputValue}
            onChange={(e) => {
              setInputValue(e.target.value);
              setShowNoDataMessage(false);
            }}
            sx={{ mt: 1.5, ml: { xs: 0, md: 1 } }}
            InputLabelProps={{
              required: true,
            }}
          />
          <LoadingButton
            size="medium"
            color="primary"
            loading={loading}
            loadingIndicator={
              <CircularProgress size={20} sx={{ color: "#fff" }} />
            }
            variant="contained"
            type="button"
            onClick={getProviderData}
            disabled={!inputValue}
            sx={{ minWidth: "180px", mt: 1.5, ml: { xs: 0, md: 1 } }}
          >
            Search
          </LoadingButton>
        </Stack>
      </Card>
      {(invoiceToManage?.length || 0) !== 0 && (
        <Box>
          <MUIDataTable
            title=""
            data={invoiceToManage}
            columns={purchaseColumns}
            options={options}
          />
        </Box>
      )}
      {showNoDataMessage && (
        <Typography variant="body2" color="#718096">
          No Data Found
        </Typography>
      )}

      {showSinglePurchaseDetail && (
        <PurchaseDetails
          open={showSinglePurchaseDetail}
          handleClose={() => setShowSinglePurchaseDetail(false)}
          activeInvId={activeInvId}
        />
      )}
    </div>
  );
};

export default Suppliers;
